import {
  Avatar,
  DialogActions,
  DialogContent,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { MedicationOption } from 'Components/MedicationOption';
import { NextPrevButton } from 'Components/NextPrevButton';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import {
  LeftArrowWhite,
  PenIcon,
  PurpleClose,
  PurpleVi,
  PurpleWarning,
} from 'Images';
import { useContext, useEffect, useState } from 'react';
import { OrderRegularMedicationsContext } from '../OrderRegularMedicationsContext';
import { sendEvent } from 'Utils/sendEvent';
import { MEDICATION_OPTIONS_NOT_IN_STOCK } from 'API';

interface INotAvailableMedication {
  outOfStockMedicationCode: number;
}

export const NotAvailableMedication = (props: INotAvailableMedication) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby,
  );

  const isDesktop = useIsDesktop();
  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);
  const [isPickupOption, setIsPickupOption] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const {
    handleClose,
    nextStep,
    outOfStockMedications,
    prevStep,
    setOptionToOutOfStockMedication,
    steps,
    activeStep,
    expiredMedicationOptions: outOfStockMedicationOptions,
  } = useContext(OrderRegularMedicationsContext);

  const nextStepWrapper = () => {
    if (medication && !medication.lackMedicineNote) {
      setErrorMessage(dictionary.pleaseSelectAnOption);
      return;
    }

    setErrorMessage('');

    if (activeStep === steps.length - 2 && selfPurchaseMeds.length > 0) {
      setIsPickupOption(true);
    } else if (activeStep === steps.length - 1) {
      sendEvent('Medicine', 'ContinueButtonReplaceMedicine');
      nextStep();
    } else {
      setIsPickupOption(false);
      sendEvent('Medicine', 'ContinueButtonReplaceMedicine');
      nextStep();
    }
  };

  const prevStepWrapper = () => {
    sendEvent('Medicine', 'BackButtonReplaceMedicine');
    prevStep();
  };

  useEffect(() => {
    if (!outOfStockMedications.length) {
      nextStep();
    }
  }, [nextStep, outOfStockMedications]);

  const medication = outOfStockMedications.find(
    (outOfStockMedication) =>
      outOfStockMedication.medicatioN_CODE === props.outOfStockMedicationCode,
  );

  if (medication === null || medication === undefined) return null;

  const selfPurchaseMeds = outOfStockMedications.filter(
    (med) =>
      med?.OutOfStockOptionCode ===
        MEDICATION_OPTIONS_NOT_IN_STOCK.GET_BY_MYSELF ||
      med?.OutOfStockOptionCode ===
        MEDICATION_OPTIONS_NOT_IN_STOCK.SEND_BY_POST,
  );

  const medOptions = outOfStockMedicationOptions.map((option, index) =>
    index === 1 ? null : (
      <MedicationOption
        isSelected={medication.lackMedicineNote === option.value}
        key={option.code}
        text={option.value}
        description={option.description}
        onClick={() => {
          setErrorMessage('');
          setOptionToOutOfStockMedication(
            medication,
            option.value,
            option.code,
          );
        }}
      />
    ),
  );

  const selfOptions = outOfStockMedicationOptions.map((option, index) =>
    index === 1 || index === 2 ? (
      <MedicationOption
        isSelected={selfPurchaseMeds[0]?.lackMedicineNote === option.value}
        key={option.code}
        text={index === 1 ? option.value : dictionary.selfPickup}
        description={''}
        onClick={() => {
          selfPurchaseMeds.forEach((med) =>
            setOptionToOutOfStockMedication(med, option.value, option.code),
          );
        }}
      />
    ) : null,
  );

  return (
    <>
      <DialogContent
        sx={{
          padding: isDesktop ? '32px' : '0 24px',
          backgroundColor: isDesktop ? '#FFF' : 'transparent',
          paddingBottom: isDesktop ? undefined : '49px',
          zIndex: 111,
          top: isDesktop ? undefined : '24px',
          position: isDesktop ? undefined : 'relative',
        }}
      >
        <Paper
          sx={{
            backgroundColor: 'primary.shade_50',
            borderRadius: '16px',
            padding: isDesktop ? '24px' : '16px',
            boxShadow: 'none',
            width: isDesktop ? undefined : '100%',
          }}
        >
          <Stack
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid
              container
              direction={'row'}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Typography
                variant='h3'
                component='h2'
                tabIndex={0}
                id='dialog-title'
              >
                {dictionary.requestRegularMedications}
              </Typography>

              <Avatar
                role={'button'}
                component={'button'}
                alt={dictionary.exitAndReturnToMedicationsAndPrescriptionsLobby}
                src={PurpleClose}
                onClick={handleClose}
                sx={
                  isDesktop
                    ? {
                        cursor: 'pointer',
                        bottom: '45px',
                        right: '45px',
                        border: 'none',
                        background: 'transparent',
                        width: '40px',
                        height: '40px',
                        padding: 0,
                      }
                    : {
                        alignSelf: 'flex-start',
                        cursor: 'pointer',
                        top: '-2px',
                        right: '15px',
                        border: 'none',
                        background: 'transparent',
                        width: '40px',
                        height: '40px',
                        padding: 0,
                        position: 'absolute',
                      }
                }
              />
            </Grid>
            <Divider
              aria-hidden='true'
              sx={{
                m: '12px 0 40px 0 ',
                width: '100%',
                borderColor: 'primary.shade_100',
              }}
            />
            <Typography variant='h5'>{dictionary.outOfStock}</Typography>

            {isPickupOption ? (
              <>
                <Typography
                  variant='body2'
                  sx={{
                    m: '16px 0',
                  }}
                >
                  {dictionary.selfPurchaseMedications(selfPurchaseMeds.length)}
                </Typography>
                <Stack
                  sx={{
                    justifyContent: 'center',
                    mb: 4,
                  }}
                >
                  {selfPurchaseMeds.map((med, i) => {
                    return (
                      <Stack
                        key={i}
                        sx={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: '5px',
                        }}
                      >
                        <Avatar
                          src={PurpleVi}
                          sx={{ height: '24px', width: '24px', mr: 1 }}
                          alt=''
                        />
                        <Typography variant='h5'>
                          {med.medicatioN_NAME}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </>
            ) : (
              <>
                <Typography
                  variant='body2'
                  sx={{
                    mb: 2,
                    mt: 1,
                  }}
                >
                  {activeStep}
                  {dictionary.outOf}
                  {steps.length - 2}
                </Typography>
                <Stack
                  sx={{
                    backgroundColor: 'primary.shade_100',
                    border: '1px solid',
                    borderColor: 'primary.shade_200',
                    borderRadius: '12px',
                    maxWidth: '420px',
                    width: 'auto',
                    p: isDesktop ? '24px' : '16px 12px',
                    mb: 4,
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      textAlign: 'center',
                      mb: 1,
                    }}
                  >
                    {medication.medicatioN_NAME}
                  </Typography>

                  <Stack
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: isDesktop ? 1 : '4px',
                    }}
                  >
                    <Avatar
                      src={PurpleWarning}
                      alt={generalDictionary.warning}
                      sx={
                        isDesktop
                          ? { width: '24px', height: '24px' }
                          : { width: '20px', height: '20px' }
                      }
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        fontSize: isDesktop ? undefined : '17px',
                      }}
                    >
                      {dictionary.weAreSorry}
                    </Typography>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>

          <Stack
            spacing={1.5}
            sx={{
              alignItems: 'center',
              p: isDesktop ? undefined : '0 14px',
            }}
          >
            {isPickupOption ? (
              <>
                <Typography
                  variant='body2'
                  sx={{
                    mb: 1,
                  }}
                >
                  {dictionary.pickupChoose}
                </Typography>
                {selfOptions}
              </>
            ) : (
              <>
                <Typography
                  variant='body2'
                  sx={{
                    mb: 1,
                  }}
                >
                  {dictionary.whatWouldYouLikeToDo}
                </Typography>
                {medOptions}
              </>
            )}
          </Stack>
        </Paper>
      </DialogContent>
      {errorMessage && (
        <Typography
          role='alert'
          color='error'
          sx={{
            mt: 2,
            mr: 2,
            textAlign: 'center',
            fontSize: isDesktop ? undefined : '16px',
            fontWeight: 400,
            position: 'relative',
            top: '-10px',
          }}
        >
          {errorMessage}
        </Typography>
      )}
      <DialogActions
        sx={
          isDesktop
            ? {
                backgroundColor: '#fff',
                padding: '40px',
              }
            : {
                justifyContent: 'center',
                backgroundColor: '#fff',
                flexDirection: 'row',
                position: 'relative',
                bottom: '10px',
                width: '100%',
                zIndex: 999,
                mb: isDesktop ? undefined : '24px',
                gap: '8px',
                padding: '0 16px',
              }
        }
      >
        <NextPrevButton
          text={generalDictionary.editOrder}
          isNext={false}
          callback={prevStepWrapper}
          hideArrowIcon
          icon={
            <Avatar
              src={PenIcon}
              alt=''
              sx={{
                width: '21px',
                height: '20px',
                ml: '5px',
              }}
            />
          }
          style={{
            button: {
              p: isDesktop ? '8px 48px' : '4px 16px',
              width: isDesktop ? '230px' : 'calc(50% - 4px)',
              height: isDesktop ? '46px' : '38px',
              maxWidth: '100%',
            },
            text: {
              fontSize: isDesktop ? '18px' : '16px',
              fontWeight: 700,
            },
          }}
        />
        <NextPrevButton
          text={generalDictionary.sendOrder}
          isNext
          isBlue
          callback={isPickupOption ? nextStep : nextStepWrapper}
          hideArrowIcon
          icon={
            <Avatar
              src={LeftArrowWhite}
              alt=''
              sx={{
                width: '21px',
                height: '20px',
              }}
            />
          }
          style={{
            button: {
              padding: isDesktop ? '8px 48px' : '5px 24px',
              width: isDesktop ? '230px' : 'calc(50% - 4px)',
              height: isDesktop ? '46px' : '38px',
              mr: isDesktop ? '32px !important' : undefined,
              maxWidth: '100%',
            },
            text: {
              fontSize: isDesktop ? '18px' : '16px',
              fontWeight: 600,
            },
          }}
        />
      </DialogActions>
    </>
  );
};
